import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { useEffect, useRef, useState } from "react";
import { addDragToElement } from "./dragUtils";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const PdfViewer = ({
  fileBlob,
  signatureFields,
  setSignatureFields,
  setViewportInfo,
  fontSettings,
  selectedField,
  setSelectedField,
  signingMode,
  autoSave,
}) => {
  const containerRef = useRef();
  const [pdfRendered, setPdfRendered] = useState(false);

  // =========================
  // PDF RENDERING
  // =========================
  useEffect(() => {
    if (!fileBlob || pdfRendered) return;

    const renderPdf = async () => {
      const loadingTask = pdfjsLib.getDocument(fileBlob);
      const pdf = await loadingTask.promise;

      const container = containerRef.current;
      container.innerHTML = "";

      const firstPage = await pdf.getPage(1);
      const viewport = firstPage.getViewport({ scale: 1.3 });

      setViewportInfo({
        renderedWidth: viewport.width,
        renderedHeight: viewport.height,
        scale: 1.2,
      });

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.3 });

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;

        const img = document.createElement("img");
        img.src = canvas.toDataURL();
        img.style.width = "80%";

        const wrapper = document.createElement("div");
        wrapper.style.position = "relative";
        wrapper.style.marginBottom = "0px";
        wrapper.style.width = `${viewport.width}px`;
        wrapper.style.height = `${viewport.height}px`;

        const overlay = document.createElement("div");
        overlay.style.position = "absolute";
        overlay.style.top = 0;
        overlay.style.left = 0;
        overlay.style.width = `${viewport.width}px`;
        overlay.style.height = `${viewport.height}px`;
        overlay.style.cursor = "crosshair";
        overlay.dataset.pageIndex = i - 1;

        wrapper.appendChild(img);
        wrapper.appendChild(overlay);
        container.appendChild(wrapper);
      }

      setPdfRendered(true);
    };

    renderPdf();
  }, [fileBlob, pdfRendered]);

  // =========================
  // RENDER SIGNATURE FIELDS
  // =========================
  useEffect(() => {
    if (!fileBlob || !pdfRendered) return;
    const container = containerRef.current;
    if (!container) return;

    const overlays = container.querySelectorAll(
      "div[style*='position: absolute']",
    );

    overlays.forEach((overlay) => {
      if (!overlay.dataset.pageIndex) return;

      overlay.innerHTML = "";
      const pageIndex = Number(overlay.dataset.pageIndex);

      const pageFields = signatureFields.filter(
        (f) => f.pageIndex === pageIndex,
      );

      pageFields.forEach((fieldData) => {
        if (fieldData.imageData) {
          const containerDiv = document.createElement("div");
          containerDiv.style.position = "absolute";
          containerDiv.style.left = `${fieldData.x}px`;
          containerDiv.style.top = `${fieldData.y}px`;
          containerDiv.style.display = "flex";
          containerDiv.style.flexDirection = "column";
          containerDiv.style.alignItems = "center";
          containerDiv.style.gap = "4px";

          const imgEl = document.createElement("img");
          imgEl.src = fieldData.imageData;
          imgEl.style.width = "150px";
          imgEl.style.height = "auto";
          imgEl.style.pointerEvents = "none";

          const iconContainer = document.createElement("div");
          iconContainer.style.display = "flex";
          iconContainer.style.gap = "6px";
          iconContainer.style.pointerEvents = "auto";

          const deleteIcon = document.createElement("span");
          deleteIcon.textContent = "❌";
          deleteIcon.className = "field-icon";
          deleteIcon.style.cursor = "pointer";
          deleteIcon.style.fontSize = "14px";
          deleteIcon.style.padding = "2px";
          deleteIcon.style.backgroundColor = "#ffebee";
          deleteIcon.style.borderRadius = "3px";

          deleteIcon.addEventListener("click", (e) => {
            e.stopPropagation();
            setSignatureFields((prev) =>
              prev.filter((f) => f.id !== fieldData.id),
            );
          });

          const saveIcon = document.createElement("span");
          saveIcon.innerHTML = "✅";
          saveIcon.className = "field-icon";
          saveIcon.style.cursor = "pointer";
          saveIcon.style.fontSize = "14px";
          saveIcon.style.padding = "2px";
          saveIcon.style.backgroundColor = "#e8f5e8";
          saveIcon.style.borderRadius = "3px";

          saveIcon.addEventListener("click", (e) => {
            e.stopPropagation();
            setSignatureFields((prev) =>
              prev.map((f) =>
                f.id === fieldData.id ? { ...f, saved: true } : f,
              ),
            );
          });

          iconContainer.appendChild(saveIcon);
          iconContainer.appendChild(deleteIcon);

          containerDiv.appendChild(imgEl);
          containerDiv.appendChild(iconContainer);

          if (fieldData.saved) {
            saveIcon.style.display = "none";
            deleteIcon.style.display = "none";
          }

          addDragToElement(
            containerDiv,
            overlay,
            pageIndex,
            fieldData.id,
            setSignatureFields, // ✅ ADD THIS
          );
          overlay.appendChild(containerDiv);
        } else if (fieldData.textData) {
          const containerDiv = document.createElement("div");
          containerDiv.style.position = "absolute";
          containerDiv.style.left = `${fieldData.x}px`;
          containerDiv.style.top = `${fieldData.y}px`;
          containerDiv.style.padding = "4px 8px";
          containerDiv.style.backgroundColor = "#eee";
          containerDiv.style.border = "1px solid #333";
          containerDiv.style.borderRadius = "4px";
          containerDiv.style.zIndex = "10";
          containerDiv.style.display = "flex";
          containerDiv.style.alignItems = "center";
          containerDiv.style.gap = "6px";

          const textEl = document.createElement("div");
          textEl.textContent = fieldData.textData;
          textEl.style.pointerEvents = "none";

          containerDiv.appendChild(textEl);

          addDragToElement(
            containerDiv,
            overlay,
            pageIndex,
            fieldData.id,
            setSignatureFields, // ✅ ADD THIS
          );
          overlay.appendChild(containerDiv);
        }
      });
    });
  }, [signatureFields, fileBlob, pdfRendered]);

  // =========================
  // UPDATE FONT WHEN EDITING
  // =========================
  useEffect(() => {
    if (!fileBlob || !pdfRendered || !selectedField) return;

    const container = containerRef.current;
    if (!container) return;

    const fieldElement = container.querySelector(
      `[data-id="${selectedField}"]`,
    );

    if (fieldElement) {
      const textElement = fieldElement.querySelector("div");
      if (textElement) {
        textElement.style.fontFamily = fontSettings.fontType;
        textElement.style.fontSize = `${fontSettings.fontSize}px`;
        textElement.style.color = fontSettings.fontColor;
      }
    }
  }, [fontSettings, selectedField, fileBlob, pdfRendered]);

  // =========================
  // HANDLE DROP
  // =========================
  //   const handleDrop = (event) => {
  //     event.preventDefault();

  //     const data = event.dataTransfer.getData("text/plain");
  //     if (!data) return;

  //     const container = containerRef.current;
  //     const overlays = container.querySelectorAll(
  //       "div[style*='position: absolute']",
  //     );

  //     for (let index = 0; index < overlays.length; index++) {
  //       const overlay = overlays[index];
  //       const rect = overlay.getBoundingClientRect();

  //       if (
  //         event.clientX >= rect.left &&
  //         event.clientX <= rect.right &&
  //         event.clientY >= rect.top &&
  //         event.clientY <= rect.bottom
  //       ) {
  //         const x = event.clientX - rect.left;
  //         const y = event.clientY - rect.top;
  //         const newId = Date.now();
  //         const isImage = data.startsWith("data:image/");

  //         const wrapper = document.createElement("div");
  //         wrapper.style.position = "absolute";
  //         wrapper.style.left = `${x}px`;
  //         wrapper.style.top = `${y}px`;
  //         wrapper.style.cursor = "move";
  //         wrapper.style.zIndex = "10";

  //         if (isImage) {
  //           const imgEl = document.createElement("img");
  //           imgEl.src = data;
  //           imgEl.style.width = "150px";
  //           imgEl.style.height = "auto";
  //           wrapper.appendChild(imgEl);
  //         } else {
  //           wrapper.textContent = data;
  //         }

  //         addDragToElement(wrapper, overlay, index, newId);
  //         overlay.appendChild(wrapper);

  //         setSignatureFields((prev) => [
  //           ...prev,
  //           {
  //             id: newId,
  //             x,
  //             y,
  //             pageIndex: index,
  //             saved: false,
  //             imageData: isImage ? data : null,
  //             textData: !isImage ? data : null,
  //           },
  //         ]);

  //         break;
  //       }
  //     }
  //   };

  const handleDrop = (event) => {
    event.preventDefault();

    const data = event.dataTransfer.getData("text/plain");
    if (!data) return;

    const container = containerRef.current;
    const overlays = container.querySelectorAll(
      "div[style*='position: absolute']",
    );

    for (let index = 0; index < overlays.length; index++) {
      const overlay = overlays[index];
      const rect = overlay.getBoundingClientRect();

      if (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      ) {
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const isImage = data.startsWith("data:image/");

        // Handle all_pages signingMode
        if (signingMode === "all_pages") {
          const newFields = [];

          overlays.forEach((ov, pageIdx) => {
            const newId = Date.now() + Math.random();

            const newField = {
              id: newId,
              x,
              y,
              pageIndex: pageIdx,
              saved: true,
              imageData: isImage ? data : null,
              textData: !isImage ? data : null,
            };

            newFields.push(newField);

            const wrapper = document.createElement("div");
            wrapper.style.position = "absolute";
            wrapper.style.left = `${x}px`;
            wrapper.style.top = `${y}px`;
            wrapper.style.cursor = "move";
            wrapper.style.zIndex = "10";

            if (isImage) {
              const imgEl = document.createElement("img");
              imgEl.src = data;
              imgEl.style.width = "150px";
              imgEl.style.height = "auto";
              wrapper.appendChild(imgEl);
            } else {
              wrapper.textContent = data;
            }

            // ✅ IMPORTANT FIX
            ov.appendChild(wrapper);

            addDragToElement(
              wrapper,
              ov, // ✅ use ov
              pageIdx,
              newId,
              setSignatureFields,
            );
          });

          // Update the signature fields state
          setSignatureFields((prev) => [...prev, ...newFields]);
        } else {
          // Normal single page behavior
          const newId = Date.now();

          const newField = {
            id: newId,
            x,
            y,
            pageIndex: index,
            saved: false,
            imageData: isImage ? data : null,
            textData: !isImage ? data : null,
          };

          setSignatureFields((prev) => [...prev, newField]);

          // Create the wrapper element
          const wrapper = document.createElement("div");
          wrapper.style.position = "absolute";
          wrapper.style.left = `${x}px`;
          wrapper.style.top = `${y}px`;
          wrapper.style.cursor = "move";
          wrapper.style.zIndex = "10";

          if (isImage) {
            const imgEl = document.createElement("img");
            imgEl.src = data;
            imgEl.style.width = "150px";
            imgEl.style.height = "auto";
            wrapper.appendChild(imgEl);
          } else {
            wrapper.textContent = data;
          }

          overlay.appendChild(wrapper); // Add the new element to the overlay

          // Call the addDragToElement function to make the new element draggable
          addDragToElement(wrapper, overlay, index, newId, setSignatureFields);
        }

        break;
      }
    }
  };
  // =========================
  // DRAG LOGIC
  // =========================
  //   function addDragToElement(element, overlay, pageIndex, fieldId) {
  //     let offsetX, offsetY;
  //     let isDragging = false;

  //     element.dataset.id = fieldId;

  //     element.addEventListener("mousedown", (e) => {
  //       if (e.target.closest(".field-icon")) return;

  //       isDragging = true;

  //       const rect = element.getBoundingClientRect();
  //       offsetX = e.clientX - rect.left;
  //       offsetY = e.clientY - rect.top;

  //       document.addEventListener("mousemove", onMouseMove);
  //       document.addEventListener("mouseup", onMouseUp);
  //     });

  //     function onMouseMove(e) {
  //       if (!isDragging) return;

  //       const overlayRect = overlay.getBoundingClientRect();

  //       let newX = e.clientX - offsetX - overlayRect.left;
  //       let newY = e.clientY - offsetY - overlayRect.top;

  //       newX = Math.max(
  //         0,
  //         Math.min(newX, overlayRect.width - element.offsetWidth),
  //       );
  //       newY = Math.max(
  //         0,
  //         Math.min(newY, overlayRect.height - element.offsetHeight),
  //       );

  //       element.style.left = `${newX}px`;
  //       element.style.top = `${newY}px`;
  //     }

  //     function onMouseUp() {
  //       if (!isDragging) return;

  //       isDragging = false;

  //       setSignatureFields((prev) =>
  //         prev.map((field) => {
  //           if (field.id === fieldId) {
  //             return {
  //               ...field,
  //               x: parseFloat(element.style.left),
  //               y: parseFloat(element.style.top),
  //               pageIndex,
  //             };
  //           }
  //           return field;
  //         }),
  //       );

  //       document.removeEventListener("mousemove", onMouseMove);
  //       document.removeEventListener("mouseup", onMouseUp);
  //     }
  //   }

  return (
    <div
      style={{
        flex: 2,
        border: "1px solid #ddd",
        padding: "10px",
        backgroundColor: "#fff",
        maxHeight: "80vh",
        overflowY: "auto",
      }}
    >
      <div
        ref={containerRef}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        style={{
          background: "white",
          minHeight: "100vh",
          position: "relative",
        }}
      />
    </div>
  );
};

export default PdfViewer;
